import React, { Component } from 'react';
import { Map as LeafletMap, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import tinycolor from "tinycolor2";
import { stopIcon } from '../../../components/leaflet/StopIcon';

export class DashboardMap extends Component {
    constructor(props) {
        super(props);

        this.map = null;
        this.markerLayer = null;
        this.polylineLayer = null;

        this.coordsCache = {};
        this.colorCache = {};
        this.cacheSize = 0;
        this.boundsCache = null;
        this.lastBoundsStops = null;

        this.state = { selectedPath: null };

        this.canvasRenderer = null;
        this.polylinesById = {};
        this._polylineUpdateTimer = null;

        this.handlePolylineClick = this.handlePolylineClick.bind(this);
        this.onPolylineClickShared = this.onPolylineClickShared.bind(this)
    }

    componentDidMount() {
        this.map = this.mapRef.leafletElement;

        this.markerLayer = L.markerClusterGroup({
            minClusterSize: 10,
            iconCreateFunction: function (cluster) {
                const count = cluster.getChildCount();
                const color = count > 50 ? "#cf4c4c" : count > 20 ? "#cf8a4c" : "#4c89cf";
                const size = count > 50 ? 55 : count > 20 ? 48 : 40;

                return L.divIcon({
                    html: `<div class="cluster-circle" style="
                width:${size}px;
                height:${size}px;
                background:${color};
                color:white;
                border-radius:50%;
                display:flex;
                align-items:center;
                justify-content:center;
                font-size:14px;
                font-weight:600;
            ">${count}</div>`,
                    className: "custom-cluster-icon",
                    iconSize: [size, size]
                });
            },

            spiderfyOnMaxZoom: true,
            showCoverageOnHover: false,
            zoomToBoundsOnClick: true,
            maxClusterRadius: 50,
            disableClusteringAtZoom: 14,
            spiderfyDistanceMultiplier: 2,
            chunkedLoading: true,
            chunkInterval: 200,
            chunkDelay: 50
        });

        this.canvasRenderer = L.canvas({ padding: 0.5, tolerance: 15 });
        this.polylineLayer = L.layerGroup({ pane: 'overlayPane' });

        this.map.addLayer(this.markerLayer);
        this.map.addLayer(this.polylineLayer);

        this.updateMarkers(this.props.mapStops, this.props.showStops);
        this.updatePolylines(this.props.mapPaths);
    }

    shouldComponentUpdate(nextProps, nextState) {
        // Prevent unnecessary re-renders
        return (
            this.props.mapStops !== nextProps.mapStops ||
            this.props.mapPaths !== nextProps.mapPaths ||
            this.props.showStops !== nextProps.showStops ||
            this.state.selectedPath !== nextState.selectedPath
        );
    }

    componentDidUpdate(prevProps, prevState) {
        const mapStops = this.props.mapStops;
        const mapPaths = this.props.mapPaths;
        const showStops = this.props.showStops;

        if (mapStops !== prevProps.mapStops || showStops !== prevProps.showStops) {
            this.updateMarkers(mapStops, showStops);
        }

        if (mapPaths !== prevProps.mapPaths) {
            this.updatePolylines(mapPaths);
        } else if (this.state.selectedPath !== prevState.selectedPath) {
            this.updatePolylineSelectionStyles(prevState.selectedPath, this.state.selectedPath);
        }
    }

    componentWillUnmount() {
        // Clean up caches
        this.coordsCache = {};
        this.colorCache = {};
        this.cacheSize = 0;
        this.boundsCache = null;
        this.lastBoundsStops = null;

        if (this.markerLayer) {
            this.markerLayer.clearLayers();
        }
        if (this.polylineLayer) {
            this.polylineLayer.clearLayers();
        }
    }

    updateMarkers(mapStops, showStops) {
        if (!this.markerLayer) {
            return;
        }

        this.markerLayer.clearLayers();

        if (!showStops || !mapStops || mapStops.length === 0) {
            return;
        }

        const markers = [];
        for (let i = 0; i < mapStops.length; i++) {
            const stop = mapStops[i];

            if (!stop || !stop.position) {
                continue;
            }

            const marker = L.marker(stop.position, { icon: stopIcon })
                .bindTooltip(stop.name + " (" + stop.code + ")", {
                    sticky: true,
                    direction: 'top'
                });

            markers.push(marker);
        }

        this.markerLayer.addLayers(markers);
    }


    updatePolylineSelectionStyles(prevSelectedId, newSelectedId) {
        if (prevSelectedId && this.polylinesById[prevSelectedId]) {
            const prevPoly = this.polylinesById[prevSelectedId];
            prevPoly.setStyle({ weight: 4 });
            prevPoly.setStyle({ color: this.getPathColorCached(prevPoly, false) });
        }

        if (newSelectedId && this.polylinesById[newSelectedId]) {
            const newPoly = this.polylinesById[newSelectedId];
            newPoly.setStyle({ weight: 6 });
            newPoly.setStyle({ color: this.getPathColorCached(newPoly, true) });
        }
    }

    updatePolylines(mapPaths) {
        if (!this.polylineLayer) {
            return;
        }

        // Debounce small bursts of updates
        if (this._polylineUpdateTimer) {
            clearTimeout(this._polylineUpdateTimer);
        }

        this._polylineUpdateTimer = setTimeout(() => {
            requestAnimationFrame(() => {
                const newIds = new Set();

                // Build lookup for incoming paths
                const incomingById = {};
                for (let i = 0; i < (mapPaths || []).length; i++) {
                    const p = mapPaths[i];
                    if (undefined !== p && undefined !== p.id) {
                        incomingById[p.id] = p;
                    }
                }

                // Find removed polylines
                Object.keys(this.polylinesById).forEach(id => {
                    if (!incomingById[id]) {
                        const poly = this.polylinesById[id];
                        if (poly) {
                            this.polylineLayer.removeLayer(poly);
                        }
                        delete this.polylinesById[id];
                    }
                });

                // Add / update
                for (let i = 0; i < (mapPaths || []).length; i++) {
                    const path = mapPaths[i];
                    if (!path || !path.id) {
                        continue;
                    }

                    newIds.add(path.id);

                    const coords = this.extractCoordsCached(path);
                    if (!coords || coords.length === 0) {
                        continue;
                    }

                    const isSelected = this.state.selectedPath === path.id;
                    const color = this.getPathColorCached(path, isSelected);
                    const style = { color: color, weight: isSelected ? 6 : 4, interactive: true };

                    const existing = this.polylinesById[path.id];
                    if (existing) {
                        // update geometry and style
                        existing.setLatLngs(coords);
                        existing.setStyle(style);
                    } else {
                        const poly = L.polyline(coords, {
                            ...style,
                            bubblingMouseEvents: false,
                            renderer: this.canvasRenderer
                        });
                        // store id on options so shared handler can read it
                        poly.options.pathId = path.id;
                        poly.on('click', this.onPolylineClickShared);
                        poly.bindTooltip(path.lineCode + " (" + path.code + ")", { sticky: true, direction: 'top' });
                        this.polylinesById[path.id] = poly;
                        poly.addTo(this.polylineLayer);
                    }
                }
            });
        }, 80); // 80ms debounce
    }

    onPolylineClickShared = (e) => {
        const id = e && e.target && e.target.options && e.target.options.pathId;
        if (id) this.handlePolylineClick(id);
    };

    handlePolylineClick(pathId) {
        this.setState({ selectedPath: pathId });
    }

    extractCoordsCached(path) {
        if (!path || !path.id) {
            return this.extractCoords(path);
        }

        const cacheKey = path.id + '_' + (path.shape ? JSON.stringify(path.shape) : '');

        if (this.coordsCache[cacheKey]) {
            return this.coordsCache[cacheKey];
        }

        const coords = this.extractCoords(path);
        this.coordsCache[cacheKey] = coords;
        this.cacheSize++;

        // Limit cache size to prevent memory issues
        if (this.cacheSize > 500) {
            const keys = Object.keys(this.coordsCache);
            if (keys.length > 0) {
                delete this.coordsCache[keys[0]];
                this.cacheSize--;
            }
        }

        return coords;
    }

    extractCoords(path) {
        if (path && path.shape && path.shape.segments && path.shape.segments.length > 0) {
            const coords = [];
            const segments = path.shape.segments;

            for (let s = 0; s < segments.length; s++) {
                const seg = segments[s];
                if (!seg || !seg.breakpoints) {
                    continue;
                }

                const breakpoints = seg.breakpoints;
                for (let b = 0; b < breakpoints.length; b++) {
                    const bp = breakpoints[b];
                    if (bp && bp.latitude && bp.longitude) {
                        coords.push([bp.latitude, bp.longitude]);
                    }
                }
            }

            return coords;
        }

        return [path.origin, path.destination];
    }

    getPathColorCached(path, isSelected) {
        if (isSelected) return "#4c89cf";

        const cacheKey = path.id + '_' + path.lineColor + '_' + path.orientation + '_' + isSelected;

        if (this.colorCache[cacheKey]) {
            return this.colorCache[cacheKey];
        }

        const color = this.getPathColor(path, isSelected);
        this.colorCache[cacheKey] = color;

        // Limit cache size
        if (Object.keys(this.colorCache).length > 500) {
            const keys = Object.keys(this.colorCache);
            if (keys.length > 0) {
                delete this.colorCache[keys[0]];
            }
        }

        return color;
    }

    getPathColor(path, isSelected) {
        if (isSelected) return "#4c89cf";

        const rgba = path.lineColor;
        const match = rgba && rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);

        let r = 0, g = 0, b = 0, a = 1;

        if (match) {
            r = parseInt(match[1], 10);
            g = parseInt(match[2], 10);
            b = parseInt(match[3], 10);
            a = parseFloat(match[4]);
        }

        let adjusted = tinycolor({ r: r, g: g, b: b, a: a });

        if (path.orientation === "R") {
            if (r === 0 && g === 0 && b === 0) {
                adjusted = tinycolor({ r: 100, g: 100, b: 100, a: a });
            } else {
                adjusted = adjusted.darken(20);
            }
        } else if (path.orientation === "C") {
            adjusted = adjusted.brighten(20);
        }

        const final = adjusted.toRgb();
        return (
            "rgba(" +
            final.r + "," +
            final.g + "," +
            final.b + "," +
            final.a +
            ")"
        );
    }

    getBounds(mapStops) {
        // Cache bounds calculation
        if (this.lastBoundsStops === mapStops && this.boundsCache) {
            return this.boundsCache;
        }

        if (!mapStops || mapStops.length === 0) {
            this.boundsCache = [[-90, -180], [90, 180]];
            this.lastBoundsStops = mapStops;
            return this.boundsCache;
        }

        const coords = [];
        for (let i = 0; i < mapStops.length; i++) {
            const s = mapStops[i];
            if (s && s.latitude && s.longitude) {
                coords.push([s.latitude, s.longitude]);
            }
        }

        this.boundsCache = coords.length > 0 ? coords : [[-90, -180], [90, 180]];
        this.lastBoundsStops = mapStops;
        return this.boundsCache;
    }

    render() {
        const mapStops = this.props.mapStops;

        return (
            <LeafletMap
                className="map-container dashboard-map"
                zoom={13}
                maxZoom={19}
                preferCanvas={true}
                scrollWheelZoom={true}
                bounds={this.getBounds(mapStops)}
                ref={ref => (this.mapRef = ref)}
            >
                <TileLayer
                    attribution='&copy; OpenStreetMap contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    updateWhenIdle={true}
                    updateWhenZooming={false}
                    keepBuffer={2}
                />
            </LeafletMap>
        );
    }
}